<template>
  <div class="home-page">
    <!-- History Sidebar (left) using reusable component -->
    <ConversationHistory
      :conversations="conversations"
      @create-chat="startNewChat"
      @select-conversation="goToChat"
      @delete-conversation="handleDeleteConversation"
    />

    <!-- Main Content (center) -->
    <div class="home-container">
      <div class="content-scroll">
        <h1 class="welcome-title">Welcome to Probono Solution Finder!</h1>

        <p class="welcome-subtitle">
          Tell me about a problem you want to solve in your organization or
          territory.
        </p>

        <!-- Input Area (above suggestions) -->
        <div class="input-section">
          <ChatInput :disabled="false" @send="handleSuggestion" />

          <div class="suggestions-container">
            <div class="suggestions-grid">
              <button
                v-for="suggestion in suggestions"
                :key="suggestion"
                @click="handleSuggestion(suggestion)"
                class="suggestion-button"
              >
                {{ suggestion }}
              </button>
            </div>
          </div>
        </div>

        <div class="project-description">
          <h3>About Probono Solution Finder</h3>
          <p>
            Probono Solution Finder is a collaborative platform designed to help
            organizations and territories solve complex problems related to
            sustainable building and green innovation.
          </p>
          <p>Our platform guides you through:</p>
          <ul>
            <li>
              <strong>Problem Understanding:</strong> Define your challenge in
              detail
            </li>
            <li>
              <strong>Context Mapping:</strong> Identify stakeholders,
              buildings, and local resources
            </li>
            <li>
              <strong>KPI Definition:</strong> Set measurable success criteria
            </li>
            <li>
              <strong>Solution Discovery:</strong> Find relevant tools and best
              practices
            </li>
            <li>
              <strong>Implementation Planning:</strong> Create actionable
              roadmaps
            </li>
          </ul>
          <p>
            Powered by AI and Living Labs across Europe, Probono helps you
            transform your sustainability vision into reality.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import ChatInput from "@/components/ChatInput.vue";
import ConversationHistory from "@/components/ConversationHistory.vue";
import { apiClient } from "@/plugins/apiService.js";

export default defineComponent({
  name: "HomePage",
  components: {
    ChatInput,
    ConversationHistory,
  },
  data() {
    return {
      suggestions: [
        "I want to improve thermal comfort in my building",
        "How does pollution propagate in my city?",
        "What is the social impact of my building design?",
      ],
      recentProblems: [],
      conversations: [],
    };
  },
  async mounted() {
    await this.loadConversations();
    await this.loadRecentProblems();
  },
  methods: {
    async loadConversations() {
      try {
        const response = await apiClient.get("/chat?limit=50");
        this.conversations = response.data.conversations || [];
      } catch (error) {
        console.error("Error loading conversations:", error);
      }
    },

    async loadRecentProblems() {
      try {
        const response = await apiClient.get("/problem");
        this.recentProblems = (response.data || []).slice(0, 3);
      } catch (error) {
        console.error("Error loading problems:", error);
      }
    },

    startNewChat() {
      // Reload conversations and scroll to top
      this.loadConversations();
    },

    async handleSuggestion(suggestion) {
      try {
        // Create new conversation
        const response = await apiClient.post("/chat");
        const conversationId = response.data.conversation_id;

        // Navigate to chat page and send the message
        this.$router.push({
          name: "ChatPage",
          params: { id: conversationId },
          query: { initialMessage: encodeURIComponent(suggestion) },
        });
      } catch (error) {
        console.error("Error creating conversation:", error);
        alert("Failed to start conversation. Please try again.");
      }
    },
    async goToChat(conversationId) {
      // Navigate to the conversation page
      this.$router.push({
        name: "ChatPage",
        params: { id: conversationId },
      });
    },

    handleDeleteConversation(conversationId) {
      // Remove conversation from list
      this.conversations = this.conversations.filter(
        (conv) => conv.id !== conversationId
      );
    },
  },
});
</script>

<style scoped lang="scss">
.home-page {
  height: calc(100vh - 60px);
  display: flex;
  gap: 1px;
  background-color: #f5f5f5;
  position: relative;
}

/* Center: Main Content */
.home-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
  overflow: hidden;
}

.content-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 40px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--secondaryColorLight);
    border-radius: 4px;

    &:hover {
      background: var(--secondaryColor);
    }
  }
}

.welcome-title {
  font-size: 2rem;
  font-weight: bold;
  color: var(--primaryColor);
  margin-bottom: 12px;
  text-align: center;
}

.welcome-subtitle {
  font-size: 1.1rem;
  color: var(--darkColor);
  text-align: center;
  margin-bottom: 32px;
  line-height: 1.5;
}

.suggestions-container {
  text-align: center;
  margin: 40px;
}
.suggestions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.suggestion-button {
  padding: 14px 16px;
  background-color: white;
  border: 2px solid var(--secondaryColor);
  border-radius: 10px;
  color: var(--darkColor);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  text-align: center;
  line-height: 1.4;

  &:hover {
    border-color: var(--primaryColor);
    background-color: var(--secondaryColorLighter);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(95, 151, 122, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
}

.project-description {
  border-radius: 12px;
  margin-bottom: 32px;

  h3 {
    color: var(--primaryColor);
    margin-bottom: 12px;
    font-size: 1.2rem;
  }

  p {
    color: var(--darkColor);
    margin-bottom: 12px;
    line-height: 1.6;
    font-size: 0.95rem;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 16px 0;

    li {
      padding: 8px 0 8px 32px;
      position: relative;
      color: var(--darkColor);
      font-size: 0.95rem;
      line-height: 1.5;

      &::before {
        content: "✓";
        position: absolute;
        left: 0;
        color: var(--primaryColor);
        font-weight: bold;
      }
    }
  }
}

.problems-preview {
  margin-bottom: 32px;

  h3 {
    color: var(--primaryColor);
    margin-bottom: 16px;
    font-size: 1.1rem;
  }
}

.problems-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.problem-item {
  padding: 12px;
  background-color: var(--backgroundColorLight);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;

  &:hover {
    background-color: var(--secondaryColorLighter);
    border-color: var(--primaryColor);
    transform: translateY(-1px);
  }
}

.problem-title {
  font-weight: 600;
  color: var(--darkColor);
  margin-bottom: 4px;
  font-size: 0.95rem;
  word-break: break-word;
}

.problem-date {
  color: #999;
  font-size: 0.8rem;
}
.input-section {
  padding-top: 100px;
  padding-bottom: 100px;
}

@media (max-width: 1200px) {
  .history-sidebar {
    flex: 0 0 200px;
  }

  .content-scroll {
    padding: 30px;
  }
}

@media (max-width: 1024px) {
  .history-sidebar {
    display: none;
  }

  .content-scroll {
    padding: 20px;
  }
}

@media (max-width: 768px) {
  .content-scroll {
    padding: 16px;
  }

  .welcome-title {
    font-size: 1.5rem;
  }

  .welcome-subtitle {
    font-size: 1rem;
    margin-bottom: 24px;
  }

  .suggestions-grid {
    grid-template-columns: 1fr;
  }

  .input-section {
    margin-bottom: 24px;
  }
}
</style>
